import { useState } from 'react'
import { Phone, Video } from 'lucide-react'
import { Button } from '@/components/ui'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { getCallHub, type CallHubClient } from '@/features/calls/api/callHub'

export interface CallTarget {
  id: string
  displayName?: string
  /** Per-minute diamond rate (0 = free). Absent on legacy payloads. */
  callRatePerMinute?: number
  /** Master reception switch. Defaults to true. */
  callsEnabled?: boolean
}

interface CallButtonProps {
  user: CallTarget
  /** Hide entirely (the viewer's own row, like FollowButton). */
  hidden?: boolean
  /** Injectable transport; defaults to the app-scoped singleton. */
  hub?: Pick<CallHubClient, 'invite' | 'inviteVideo'>
}

/** Renders a `{placeholder}` template of the calls dictionary with a runtime value. */
export function formatCallCopy(template: string, value: number | string): string {
  return template.replace(/\{[a-zA-Z]+\}/, String(value))
}

/**
 * Call entry point on a user profile (R31 voice, R32 video). Free callees
 * invite at once; priced callees confirm the rate first (no blind
 * charges). Disabled callees render inert buttons with clear copy. Video
 * invites ride the separate `InviteVideoCall` hub method.
 */
export function CallButton({ user, hidden = false, hub }: CallButtonProps) {
  const { strings } = useLanguage()
  const copy = strings.calls
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [videoMode, setVideoMode] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (hidden) return null

  const enabled = user.callsEnabled !== false
  const rate = user.callRatePerMinute ?? 0
  const label = !enabled ? copy.callsDisabled : rate <= 0 ? copy.callFree : formatCallCopy(copy.callRate, rate)
  const videoLabel =
    !enabled ? copy.callsDisabled : rate <= 0 ? copy.videoCallFree : formatCallCopy(copy.videoCallRate, rate)

  const transport = hub ?? getCallHub()

  const sendInvite = async (withVideo: boolean) => {
    setInviting(true)
    setError(null)
    try {
      if (withVideo) await transport.inviteVideo(user.id)
      else await transport.invite(user.id)
      setConfirmOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setInviting(false)
    }
  }

  const tap = (withVideo: boolean) => {
    if (!enabled) return
    if (rate > 0) {
      setVideoMode(withVideo)
      setConfirmOpen(true)
    } else void sendInvite(withVideo)
  }

  return (
    <>
      <div className="flex w-full gap-2">
        <div className="flex-1">
          <Button
            variant={enabled ? 'filled' : 'outlined'}
            fullWidth
            disabled={!enabled || inviting}
            leftIcon={<Phone size={18} />}
            onClick={() => tap(false)}
            aria-label={label}
          >
            {label}
          </Button>
        </div>
        {enabled && (
          <button
            type="button"
            disabled={inviting}
            onClick={() => tap(true)}
            aria-label={videoLabel}
            title={videoLabel}
            className="flex shrink-0 items-center justify-center self-stretch rounded-full bg-lm-surface-container-high px-4 text-lm-on-surface disabled:opacity-50"
          >
            <Video size={20} />
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-body-sm text-lm-error">
          {error}
        </p>
      )}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6" role="dialog" aria-label={copy.confirmTitle}>
          <div className="w-full max-w-sm rounded-[20px] bg-lm-surface-container p-5">
            <h2 className="text-headline-sm font-bold text-lm-on-surface">{copy.confirmTitle}</h2>
            <p className="mt-2 text-body-md text-lm-on-surface-variant">
              {formatCallCopy(copy.confirmBody, rate)}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-label-lg text-lm-primary"
              >
                {strings.common.cancel}
              </button>
              <button
                type="button"
                disabled={inviting}
                onClick={() => void sendInvite(videoMode)}
                className="px-4 py-2 text-label-lg font-bold text-lm-primary disabled:opacity-50"
              >
                {copy.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
